import {
  WSAlert,
  WSButton,
  WSControlGroup,
  WSDivider,
  WSEmptyState,
  WSFlexBox,
  WSGrid,
  WSList,
  WSPanel,
  WSPill,
  WSSidebar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IAdditionalData,
  IInvoice
} from "@wingspanhq/payments/dist/interfaces";
import {
  IPayrollReportResponse,
  IPayrollReportInvoice
} from "@wingspanhq/payments/dist/interfaces/api/reports";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { useUserId } from "../../../../query/hooks/helpers";
import { getPayrollRunTitle } from "../../../../query/payments/selectors";
import { paymentsService } from "../../../../services/payments";
import { VerticalDivider } from "../../../../shared/components/VerticalDivider";
import { track } from "../../../../utils/analytics";
import { CardReportLoading } from "../../components/CardReportLoading/CardReportLoading";
import { DownloadReportButton } from "../../components/DownloadReportButton/DownloadReportButton";
import { useSelectColumnsModal } from "../../components/ModalSelectColumns";
import { ReportsTable } from "../../components/ReportsTable/ReportsTable";
import { useFetchReportData } from "../../hooks/useFetchReportData";
import { useReportColumns } from "../../hooks/useReportColumns";
import { ReportFormat, ReportType } from "../../types";
import { formatReportsValue } from "../../utils/formatReportsValue";
import { selectorColumnsWithAdditionalData } from "../../utils/selectorColumnsWithAdditionalData";
import { COLUMNS, PayrollTableItem } from "./columns";
import { selectorTableColumnsFromLabels } from "./selectorTableColumnsFromLabels";

export const REPORTS_PAYROLL_SELECTED_COLUMNS_STORAGE_KEY =
  "REPORTS_PAYROLL_SELECTED_COLUMNS_STORAGE_KEY";

const getSelectedIds = (values: Record<string, boolean>) => {
  return Object.keys(values).filter(key => values[key]);
};

const getSelectItemText = (invoice: IInvoice) =>
  `${getPayrollRunTitle(invoice)} (${formatMoney(
    invoice.amount,
    invoice.currency
  )})`;

const getPayrollTitleById = (id: string, invoices: IInvoice[]) => {
  const invoice =
    invoices.find(invoice => invoice.invoiceId === id) || invoices[0];

  return invoice ? getSelectItemText(invoice) : "Select payroll";
};

const SelectPayrollSidebar: React.FC<{
  onSubmit: (values: Record<string, boolean>) => void;
  values: Record<string, boolean>;
  payrollLineItemsData: IInvoice[];
}> = ({ onSubmit, payrollLineItemsData, values }) => {
  const [showPayrollSidebar, setShowPayrollSidebar] = useState(false);
  const [currentValues, setCurrentValues] = useState(values);
  const selectedIds = getSelectedIds(values);

  return (
    <>
      <WSButton.Secondary
        badgeNumber={selectedIds.length ? selectedIds.length : undefined}
        type="button"
        rightIcon="chevron-down"
        onClick={() => setShowPayrollSidebar(true)}
      >
        {selectedIds.length ? "Selected payrolls" : "Select payrolls"}
      </WSButton.Secondary>
      <WSSidebar
        size="S"
        visible={showPayrollSidebar}
        onClose={() => setShowPayrollSidebar(false)}
        header="Select payrolls"
        footer={
          <WSFlexBox wrap="nowrap" justify="flex-end">
            <WSButton.Tertiary
              size="M"
              mr="S"
              onClick={() => {
                setCurrentValues(values);
                setShowPayrollSidebar(false);
              }}
            >
              Cancel
            </WSButton.Tertiary>
            <WSButton
              disabled={
                Object.values(currentValues).filter(Boolean).length === 0
              }
              size="M"
              onClick={() => {
                onSubmit(currentValues);
                setShowPayrollSidebar(false);
              }}
            >
              Save
            </WSButton>
          </WSFlexBox>
        }
      >
        <WSPanel noBorder>
          {payrollLineItemsData.length > 0 ? (
            <WSControlGroup
              mt="NONE"
              type="checkbox"
              name="payrolls"
              value={currentValues}
              onChange={setCurrentValues}
              options={payrollLineItemsData.map(invoice => ({
                value: invoice.invoiceId,
                label: getSelectItemText(invoice)
              }))}
            />
          ) : (
            <WSText color="gray500" align="center" my="XL">
              No payrolls available
            </WSText>
          )}
        </WSPanel>
      </WSSidebar>
    </>
  );
};

export const PayrollLineItemsReportsDashboard: React.FC<{
  payrollLineItemsData: IInvoice[];
  customFieldsData: IAdditionalData[];
}> = ({ payrollLineItemsData, customFieldsData }) => {
  const userId = useUserId();
  const [selectedPayrolls, setSelectedPayrolls] = useState<
    Record<string, boolean>
  >({ [payrollLineItemsData[0]?.invoiceId]: true });
  const [showCompletionAlert, setShowCompletionAlert] = useState(false);

  const selectedPayrollIds = getSelectedIds(selectedPayrolls);
  const selectedPayrollsKey = selectedPayrollIds.join(",");

  const columns = selectorColumnsWithAdditionalData<PayrollTableItem>(
    COLUMNS,
    customFieldsData
  );

  const [selectedColumns, setSelectedColumns] = useReportColumns(
    REPORTS_PAYROLL_SELECTED_COLUMNS_STORAGE_KEY,
    userId,
    columns
  );

  const modalSelectColumns = useSelectColumnsModal();

  const history = useHistory();

  const getColumnByKey = (key: string) =>
    columns.find(_column => key === _column.value)!;

  const isRunButtonDisabled =
    selectedPayrollIds.length === 0 || !selectedColumns.length;

  const request = useCallback(
    async (pageSize, pageNumber) => {
      return await paymentsService.reports.payroll({
        filter: {
          invoiceId: {
            in: selectedPayrollIds
          }
        },
        page: {
          size: pageSize,
          number: pageNumber
        }
      });
    },
    [selectedPayrollIds]
  );

  const { query, totalRowsCount, processedRowsCount } = useFetchReportData<
    IPayrollReportInvoice,
    { summary: { listSize: number }; data: IPayrollReportResponse }
  >(["QUERY_PAYROLL_LINE_ITEMS", selectedPayrollIds], request);

  // Check if report has just completed
  React.useEffect(() => {
    if (
      !query.isFetching &&
      totalRowsCount > 0 &&
      processedRowsCount === totalRowsCount
    ) {
      setShowCompletionAlert(true);
    }
  }, [processedRowsCount, query.isFetching, totalRowsCount]);

  useEffect(() => {
    if (query.isFetching) {
      setShowCompletionAlert(false);
    }
  }, [query.isFetching]);

  useEffect(() => {
    setShowCompletionAlert(false);
  }, [selectedPayrollIds]);

  const tableData = useMemo(() => {
    let result: PayrollTableItem[] = [];

    if (query.data) {
      const reports = query.data;

      reports.forEach(invoice => {
        invoice.lineItems.forEach(lineItem => {
          const parentInvoice = payrollLineItemsData.find(
            payroll => payroll.invoiceId === invoice.payrollId
          )!;

          result.push({
            lineItem,
            invoice,
            parentInvoice,
            key: `${parentInvoice.invoiceId}-${invoice.invoiceId}-${lineItem.detail}-${lineItem.description}`
          });
        });
      });
    }

    return result;
  }, [query.data, payrollLineItemsData]);

  const isDownloadButtonDisabled = tableData.length === 0 || query.isFetching;

  const getReportJSON = () => {
    return tableData.map(data => {
      return selectedColumns.reduce((result, column) => {
        const currentColumn = getColumnByKey(column)!;
        let field = {};

        try {
          if (currentColumn?.value === "Line Item Labels") {
            field = Object.entries(data.lineItem.labels || {})?.reduce(
              (acc, [labelName, labelValue]) => {
                return {
                  ...acc,
                  [`Custom Field ${labelName}`]: formatReportsValue(labelValue)
                };
              },
              {}
            );
          } else {
            field = {
              [currentColumn?.label]:
                currentColumn.getReportCell?.(data) ||
                currentColumn?.getTableCell?.(data)
            };
          }
        } catch (e) {
          console.error(`REPORT ERROR! "${currentColumn?.label}": `, e);
        }

        return { ...result, ...field };
      }, {});
    });
  };

  return (
    <>
      <WSDivider mb="M" />
      <WSGrid>
        <WSGrid.Item span={{ s: "4" }}>
          <SelectPayrollSidebar
            payrollLineItemsData={payrollLineItemsData}
            values={selectedPayrolls}
            onSubmit={setSelectedPayrolls}
          />
        </WSGrid.Item>
        <WSGrid.Item span={{ s: "8" }}>
          <WSFlexBox.CenterY>
            <WSFlexBox.CenterY
              onClick={() =>
                modalSelectColumns.open({
                  selectedColumns,
                  columns,
                  onSubmit: ({ columns }) => {
                    setSelectedColumns(columns);
                  }
                })
              }
              data-testid="selectColumns"
            >
              <WSButton.Link icon="menu">Selected columns?</WSButton.Link>
              <WSPill ml="M" theme="blue" text={`${selectedColumns.length}`} />
            </WSFlexBox.CenterY>

            <VerticalDivider mx="XL" />
            <WSButton.Primary
              name="runReport"
              disabled={isRunButtonDisabled}
              onAsyncClick={async () => {
                track("Report Ran", {
                  reportName: ReportType.PayrollLineItems,
                  selectedColumnsCount: selectedColumns.length,
                  selectedColumns,
                  filters: { selectedPayrolls: selectedPayrollIds }
                });
                await query.refetchData();
              }}
            >
              Run report
            </WSButton.Primary>
          </WSFlexBox.CenterY>
        </WSGrid.Item>
      </WSGrid>
      <WSDivider mt="M" mb="XL" />
      <WSList gap="XL">
        <WSFlexBox.CenterY justify="flex-end">
          <DownloadReportButton
            disabled={isDownloadButtonDisabled}
            getData={getReportJSON}
            onClick={(format: ReportFormat) => {
              track("Report Downloaded", {
                reportName: ReportType.PayrollLineItems,
                selectedColumnsCount: selectedColumns.length,
                selectedColumns,
                filters: { selectedPayrolls: selectedPayrollIds },
                format
              });
            }}
            fileName={`Wingspan Payroll Report`}
            sheetName={`Sheet ${getPayrollTitleById(
              selectedPayrollIds[0],
              payrollLineItemsData
            )}`.slice(0, 30)}
          />
        </WSFlexBox.CenterY>

        {showCompletionAlert && (
          <WSAlert
            theme="success"
            icon="check-circle"
            onDismiss={() => setShowCompletionAlert(false)}
          >
            Report complete
          </WSAlert>
        )}

        {query.isFetching && (
          <CardReportLoading
            processedRowsCount={processedRowsCount}
            totalRowsCount={totalRowsCount}
          />
        )}

        {tableData.length ? (
          <ReportsTable<PayrollTableItem>
            getGroupName={item => {
              return `Invoice #${item.data.invoice.invoiceNumber}, ${formatMoney(
                item.data.invoice.amount,
                // TODO(artemk): fix type
                item.data.invoice.currency as Currency
              )}`;
            }}
            columns={selectorTableColumnsFromLabels(
              selectedColumns.map(getColumnByKey).filter(Boolean),
              tableData
            )}
            tableData={tableData.map((data, i) => ({
              data,
              id: `id_${i}_${data.key}`
            }))}
          />
        ) : (
          <WSEmptyState
            type="documents"
            title="No report to show yet"
            description={
              query.data?.length === 0
                ? `No payroll details available`
                : `Select a payroll date then click 'Run report'`
            }
            orientation="horizontal"
            hasBorder
          />
        )}
      </WSList>
    </>
  );
};
